import type { StatusId } from "./statuses.ts";

/**
 * Habilidades das classes (Fase 8, doc 09 §3.3): números de partida (a calibrar em playtest). A regra
 * mora aqui; o servidor executa as primitivas (F65) e o cliente desenha pela forma da classe e a cor do
 * dono. Sem mana: a ativa tem recarga, a ultimate tem carga (0 a 100).
 */
export const SKILL_SLOTS = ["passiva", "ativa", "ultimate"] as const;
export type SkillSlot = (typeof SKILL_SLOTS)[number];

/** Geometria dos efeitos de cada classe (o "o quê" da regra "cor = quem, forma = o quê"). */
export type SkillShape = "hexagonos" | "triangulos" | "circulos" | "linhas";

/**
 * Ícone da classe pela forma (F76: quadro do grupo e cartas): o contorno, que não colide com as gemas (formas
 * cheias, ▲ ● ■ ★ ◆) nem com o hexágono cheio do slot (⬢).
 */
export const SHAPE_GLYPHS: Readonly<Record<SkillShape, string>> = { hexagonos: "⬡", triangulos: "△", circulos: "◯", linhas: "≡" };

export interface SkillDef {
  readonly id: string;
  readonly name: string;
  readonly slot: SkillSlot;
  /** Texto curto para a UI (o painel de Habilidades e as dicas). */
  readonly description: string;
  /** Recarga da ativa (ms). */
  readonly cooldownMs?: number;
  /** Carga que a ultimate pede (sempre 100). */
  readonly charge?: number;
  /** Status que aplica (alimenta as sinergias e o Elo). */
  readonly applies: readonly StatusId[];
  readonly shape: SkillShape;
  /** Números da regra (m e ms): raio, alcance, durações. O servidor lê daqui (F65); `lightMs`, o clarão do cliente (F77). */
  readonly params?: Readonly<Record<string, number>>;
}

export const SKILLS: readonly SkillDef[] = [
  // Baluarte
  { id: "escudo-erguido", name: "Escudo Erguido", slot: "passiva", shape: "hexagonos", applies: [], description: "Andando sem atacar, −30 % de dano de frente; cada golpe aparado dá +2 de Égide aos aliados a até 6 m e carga como o dano absorvido." },
  { id: "brado", name: "Brado", slot: "ativa", shape: "hexagonos", cooldownMs: 12000, applies: ["provocado"], params: { radius: 8, tauntMs: 4000, guardMs: 4000, guardMul: 0.8 }, description: "Provoca os monstros a até 8 m por 4 s (chefes: 2 s) e ganha −20 % de dano por 4 s." },
  { id: "bastiao", name: "Bastião", slot: "ultimate", shape: "hexagonos", charge: 100, applies: ["protegido"], params: { radius: 6, durationMs: 6000, pulseMs: 500, arrowPierce: 2 }, description: "Cúpula de 6 m por 6 s: aliados dentro recuperam a Égide inteira e ficam Protegidos (−25 % de dano); flechas do Patrulheiro que a cruzam perfuram." },
  // Lanceiro
  { id: "impeto", name: "Ímpeto", slot: "passiva", shape: "triangulos", applies: [], description: "Depois de 1 s correndo, o próximo golpe causa +40 % e perfura; +25 % de postura em alvos Presos ou Marcados." },
  { id: "investida", name: "Investida", slot: "ativa", shape: "triangulos", cooldownMs: 8000, applies: [], params: { distance: 8, durationMs: 300, invulnMs: 250, width: 1.5, damage: 22, push: 7 }, description: "Avança 8 m atravessando os monstros, com dano e empurrão; 0,25 s invulnerável." },
  { id: "salto-do-falcao", name: "Salto do Falcão", slot: "ultimate", shape: "triangulos", charge: 100, applies: ["preso"], params: { speed: 15, lift: 9, radius: 5, damage: 30, rootMs: 2000, bossSlowMs: 1000, bossImmunityMs: 30000 }, description: "Salta até uns 12 m no ponto mirado; o impacto de 5 m causa dano e prende os monstros por 2 s (chefes: lentos por 1 s)." },
  // Arcanista
  { id: "ressonancia", name: "Ressonância", slot: "passiva", shape: "circulos", applies: [], description: "Orbes em alvos com status de outro jogador detonam um Eco de 3 m (40 % do dano); sozinho, vale também a própria Runa." },
  { id: "runa-de-contencao", name: "Runa de Contenção", slot: "ativa", shape: "circulos", cooldownMs: 14000, applies: ["lento", "preso"], params: { range: 15, radius: 5, slowMs: 2700, rootDelayMs: 1200, rootMs: 1500 }, description: "Círculo de 5 m a até 15 m: lento de 40 %; depois de 1,2 s, prende por 1,5 s (chefes: só lento)." },
  { id: "circulo-da-fenix", name: "Círculo da Fênix", slot: "ultimate", shape: "circulos", charge: 100, applies: [], params: { radius: 7, durationMs: 6000, pulseMs: 1000, heal: 5, patua: 2, damage: 4, reviveFraction: 0.4 }, description: "Área de 7 m por 6 s: aliados curam 5 de Vida/s e ganham Égide; uma Lápide dentro revive com 40 %." },
  // Patrulheiro
  { id: "olho-de-falcao", name: "Olho de Falcão", slot: "passiva", shape: "linhas", applies: ["marcado"], description: "Flechas com carga cheia marcam por 5 s (aliados causam +10 %); a mais de 15 m, +20 % de dano." },
  { id: "flecha-sinalizadora", name: "Flecha Sinalizadora", slot: "ativa", shape: "linhas", cooldownMs: 15000, applies: ["marcado"], params: { range: 30, radius: 10, markMs: 8000, lightMs: 8000 }, description: "Explode em 10 m: revela e marca todos os monstros por 8 s e ilumina a área (mais forte à noite)." },
  { id: "disparo-rompe-nucleo", name: "Disparo Rompe-Núcleo", slot: "ultimate", shape: "linhas", charge: 100, applies: ["exposto"], params: { windupMs: 1000, length: 40, width: 1.2, damage: 60, exposeMs: 6000 }, description: "Tiro carregado em linha de 40 m; no chefe, Exposto por 6 s (+25 % de dano e +50 % de postura do grupo)." },
];

/**
 * Carga da ultimate (doc 09 §3.3): de 0 a 100, mirando uma por noite ou por fase de chefe. Dano causado
 * (1 a cada 10), dano absorvido pela Égide ou bloqueado pelo Escudo Erguido (1 a cada 5), cura dada (1 a
 * cada 5), cada Elo (5) e, à noite, 1 a cada 3 s só por estar vivo.
 */
export const ULT_CHARGE = { max: 100, damagePer: 10, absorbedPer: 5, healPer: 5, elo: 5, nightEveryMs: 3000 } as const;

/**
 * Elo (doc 09 §3.5): uma criatura atingida por habilidades de 2 ou mais jogadores em 3 s fecha um Elo — o
 * próximo golpe nela (até 3 s) causa a mais pelo número de participantes, e cada um ganha carga. Sozinho,
 * ativa e ultimate na mesma criatura em 3 s fecham um Elo menor.
 */
export const ELO = { windowMs: 3000, bonus: [0, 0, 0.15, 0.3, 0.5] as readonly number[], solo: 0.1 } as const;

/** Bônus do Elo com `n` participantes (sozinho: o menor). */
export const eloBonus = (n: number, solo: boolean): number => (solo ? ELO.solo : (ELO.bonus[Math.min(n, ELO.bonus.length - 1)] ?? 0));

export const SKILL_BY_ID: ReadonlyMap<string, SkillDef> = new Map(SKILLS.map((s) => [s.id, s]));

/**
 * Investida do Lanceiro (F66): o que o simulador precisa (predito no cliente e no servidor) — duração e
 * recarga em segundos e a velocidade do avanço (m/s), pelos números da habilidade.
 */
export const DASH = (() => {
  const p = SKILL_BY_ID.get("investida")!;
  const duration = (p.params?.["durationMs"] ?? 300) / 1000;
  return { duration, cooldown: (p.cooldownMs ?? 8000) / 1000, speed: (p.params?.["distance"] ?? 8) / duration } as const;
})();

/** Salto do Falcão (F67): a velocidade no plano com a carga toda (o alcance) e a de subida (m/s), para o simulador. */
export const LEAP = (() => {
  const p = SKILL_BY_ID.get("salto-do-falcao")!.params!;
  return { speed: p["speed"] ?? 15, lift: p["lift"] ?? 9 } as const;
})();
